import { MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue } from 'jotai';

import { cn, useModal, useResponsiveBreakPoints } from '@restworld/utils-common';
import { BuildingOfficeIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ImageGallery } from '../legacy';
import { Heading, Button, ButtonProps, Modal, ModalContent } from '@restworld/ui-ds-base';

import {
  CompanyInvolvementCard,
  CompanyStatistics,
  CompanyServiceDetails,
  CompanyRating,
  RestaurantWorkingHours,
  RestaurantWorkingHoursProps,
  RestaurantHours,
} from './job.company.components';
import { companyProfileAtom, fullRatingAtom, fullViewAtom } from '../job-position/job-position.atoms';

type TCompanyProfileContent = {
  buttonVariant?: ButtonProps['variant'];
  buttonTitle?: string;
  buttonIcon?: ButtonProps['StartIcon'];
};

const CompanyProfileDrawer = ({
  buttonTitle,
  buttonVariant = 'secondary-reversed',
  buttonIcon,
}: TCompanyProfileContent) => {
  const { images, restaurantName } = useAtomValue(companyProfileAtom);
  const [fullView, setFullView] = useAtom(fullViewAtom);
  const [fullRating, setFullRating] = useAtom(fullRatingAtom);
  const { open, close, show } = useModal();
  const { isDesktop } = useResponsiveBreakPoints();
  const { t } = useTranslation('common');

  const imageClickHandler = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      setFullView(true);
    },
    [setFullView]
  );

  const clickHandler = useCallback(() => {
    setFullView(false);
    setFullRating(false);
  }, [setFullRating, setFullView]);

  const renderEl = useMemo(() => {
    if (fullRating) return <CompanyRating type="list" />;
    if (fullView) return <ImageGallery type="full" images={images} />;
    return (
      <div className="flex flex-col gap-5">
        <CompanyInvolvementCard />
        {images?.length ? <ImageGallery type="half" images={images} onClick={imageClickHandler} /> : null}
        <CompanyStatistics />
        <CompanyRatingDetailsWrapper>
          <CompanyRating type="single" />
          <CompanyServiceDetails />
        </CompanyRatingDetailsWrapper>
      </div>
    );
  }, [fullRating, fullView, imageClickHandler, images]);

  return (
    <>
      <ButtonWrapper>
        <Button
          onClick={show ? close : open}
          variant={buttonVariant}
          StartIcon={buttonIcon}
          // Trailing chevron (master used suffixIcon="ChevronRightIcon"); hidden on the
          // "Scopri di piu" variant, matching master's condition.
          EndIcon={!buttonTitle?.includes('Scopri di') ? ChevronRightIcon : undefined}
          fullwidth
        >
          {buttonTitle ?? t('company')}
        </Button>
      </ButtonWrapper>

      <Modal open={show} onOpenChange={(v) => !v && close()}>
        <ModalContent
          className="max-w-[100vw] lg:max-w-lg lg:min-w-[28rem] lg:max-h-full"
          variant="right"
          title={
            isDesktop ? (
              <span className="flex flex-row items-center gap-1.5 justify-center">
                <BuildingOfficeIcon className="h-5 text-primary-800" />
                <Heading align="center">{t('company')}</Heading>
              </span>
            ) : undefined
          }
        >
          <ModalWrapperHeader
            title={
              <span className="flex flex-row items-center gap-1.5 justify-center">
                <BuildingOfficeIcon className="h-5 text-primary-800" />
                <Heading align="center">{t('company')}</Heading>
              </span>
            }
            hide={!isDesktop}
          />
          <StyledBody>
            <div className="flex">
              {(fullView || fullRating) && (
                <Button StartIcon={ChevronLeftIcon} onClick={clickHandler} variant="ghost" />
              )}
              <div className="w-full">
                {restaurantName && (
                  <Heading weight="medium" align="center">
                    {restaurantName}
                  </Heading>
                )}
              </div>
            </div>
            <div className="box-border flex flex-col lg:max-w-[100%]">{renderEl}</div>
          </StyledBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const ModalWrapperHeader = ({ title, hide = true }: { title: React.ReactNode; hide: boolean }) => {
  return hide ? (
    <div className="flex  flex-row lg:justify-start  justify-center lg:gap-12 lg:mb-6 mb-3 items-center w-full">
      {title}
    </div>
  ) : null;
};

const CompanyRatingDetailsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-5', className)} {...props} />
);
const ButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full text-center [&>button]:w-full', className)} {...props} />
);

const StyledBody = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4 pb-[36px]', className)} {...props} />
);

export { CompanyProfileDrawer, RestaurantWorkingHours, RestaurantHours };
export type { RestaurantWorkingHoursProps };
